import React, { useState, useEffect } from 'react';
import { Modal, Tab, Tabs } from 'react-bootstrap';
import { FiX, FiUser, FiShoppingBag, FiShoppingCart } from 'react-icons/fi';
import { connect } from 'react-redux';
import { appActions } from '../../../redux/actions/in-app';
import { Customer, Order, Store, getStatusColor, getStatusLabel } from '../../../views/app/customers/utils/customerTypes';
import './CustomerDetailsModal.scss';

interface CustomerDetailsModalProps {
  show: boolean;
  onHide: () => void;
  customerId: string;
  getCustomerDetails: typeof appActions.GetCustomerDetails;
  getCustomerOrders: typeof appActions.GetCustomerOrders;
  getCustomerStores: typeof appActions.GetCustomerStores;
  customerDetails: any;
  customerOrders: any;
  customerStores: any;
  onEdit?: (customerId: string) => void;
}

const CustomerDetailsModal: React.FC<CustomerDetailsModalProps> = ({
  show,
  onHide,
  customerId,
  getCustomerDetails,
  getCustomerOrders,
  getCustomerStores,
  customerDetails,
  customerOrders,
  customerStores,
  onEdit,
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (show && customerId) {
      fetchCustomerData();
    }
  }, [show, customerId]);

  const fetchCustomerData = async () => {
    setLoading(true);
    try {
      await getCustomerDetails(customerId);
      await getCustomerOrders(customerId, 1, 10);
      await getCustomerStores(customerId);
    } catch (error) {
      console.error('Error fetching customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const customer: Customer | null = customerDetails?.data;
  const orders: Order[] = customerOrders?.data?.results || [];
  const stores: Store[] = customerStores?.data?.results || [];

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="customer-details-modal"
    >
      <Modal.Header>
        <Modal.Title>Customer Details</Modal.Title>
        <button className="close-button" onClick={onHide}>
          <FiX />
        </button>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="loading-state">Loading customer details...</div>
        ) : customer ? (
          <>
            <div className="customer-header">
              <div className="customer-avatar-section">
                {customer.profileImageUrl ? (
                  <img
                    src={customer.profileImageUrl}
                    alt={`${customer.firstName} ${customer.lastName}`}
                    className="customer-avatar-large"
                  />
                ) : (
                  <div className="customer-avatar-placeholder-large">
                    {customer.firstName.charAt(0)}
                    {customer.lastName.charAt(0)}
                  </div>
                )}
                <div className="customer-basic-info">
                  <h3 className="customer-full-name">
                    {customer.firstName} {customer.lastName}
                  </h3>
                  <span
                    className="customer-status-badge"
                    style={{ backgroundColor: getStatusColor(customer.status) }}
                  >
                    {getStatusLabel(customer.status)}
                  </span>
                </div>
              </div>
              {onEdit && (
                <button
                  className="edit-customer-btn"
                  onClick={() => onEdit(customer.id)}
                >
                  Edit Customer
                </button>
              )}
            </div>

            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || 'profile')}
              className="customer-tabs"
            >
              <Tab
                eventKey="profile"
                title={
                  <span>
                    <FiUser /> Profile
                  </span>
                }
              >
                <div className="tab-content-section">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Email</label>
                      <span>{customer.email}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone Number</label>
                      <span>{customer.phoneNumber}</span>
                    </div>
                    <div className="info-item">
                      <label>Date Created</label>
                      <span>{formatDate(customer.createdDate)}</span>
                    </div>
                    <div className="info-item">
                      <label>Account Status</label>
                      <span>{getStatusLabel(customer.status)}</span>
                    </div>
                  </div>

                  {customer.address && (
                    <div className="address-section">
                      <h4>Address</h4>
                      <div className="info-grid">
                        <div className="info-item full-width">
                          <label>Address</label>
                          <span>{customer.address}</span>
                        </div>
                        <div className="info-item">
                          <label>City</label>
                          <span>{customer.city || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Province</label>
                          <span>{customer.province || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Post Code</label>
                          <span>{customer.postCode || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab
                eventKey="orders"
                title={
                  <span>
                    <FiShoppingBag /> Orders ({orders.length})
                  </span>
                }
              >
                <div className="tab-content-section">
                  {orders.length > 0 ? (
                    <div className="orders-list">
                      {orders.map((order) => (
                        <div key={order.id} className="order-item">
                          <div className="order-header">
                            <span className="order-number">{order.orderNumber}</span>
                            <span className="order-status">{order.status}</span>
                          </div>
                          <div className="order-details">
                            <span className="order-date">{formatDate(order.orderDate)}</span>
                            <span className="order-total">{formatCurrency(order.total)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <FiShoppingBag />
                      <p>No orders found for this customer</p>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab
                eventKey="stores"
                title={
                  <span>
                    <FiShoppingCart /> Stores ({stores.length})
                  </span>
                }
              >
                <div className="tab-content-section">
                  {stores.length > 0 ? (
                    <div className="stores-list">
                      {stores.map((store) => (
                        <div key={store.id} className="store-item">
                          <div className="store-header">
                            <span className="store-name">{store.name}</span>
                            <span className="store-status">{store.status}</span>
                          </div>
                          <div className="store-details">
                            <span className="store-location">{store.location}</span>
                            <span className="store-contact">{store.contactEmail}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <FiShoppingCart />
                      <p>No stores found for this customer</p>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </>
        ) : (
          <div className="error-state">Failed to load customer details</div>
        )}
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  customerDetails: state.allInAppReducers.getCustomerDetailsReducer,
  customerOrders: state.allInAppReducers.getCustomerOrdersReducer,
  customerStores: state.allInAppReducers.getCustomerStoresReducer,
});

const mapDispatchToProps = {
  getCustomerDetails: appActions.GetCustomerDetails,
  getCustomerOrders: appActions.GetCustomerOrders,
  getCustomerStores: appActions.GetCustomerStores,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerDetailsModal);
